import * as React from "react";

import styled from "@emotion/styled";
import FocusLock from "react-focus-lock";
import { RemoveScroll } from "react-remove-scroll";
import { Button, FadeIn, IconClose } from "atoms";
import { useToggle } from "utils/hooks";
import { Portal } from "./Portal";

const noop = () => {};

type ModalProps = {
  /**
   * Rendered content within the modal
   */
  children: React.ReactNode;
  /**
   * Optional element to focus on when modal opens
   *
   * Defaults to first focusable element in ModalContent
   */
  initialFocusRef?: React.RefObject<HTMLElement>;
  /**
   * Control whether the modal is open from parent
   *
   * Defaults to true for the use case of rendering conditionally via:
   * {isOpen && <Modal onClose={onClose} />}
   */
  isOpen?: boolean;
  /**
   * Optional handler to run when closing modal
   */
  onClose?: (event: React.MouseEvent | React.KeyboardEvent) => void;
  /**
   * Optional boolean to show close button in top-right corner, defaults to true
   */
  showCloseButton?: boolean;
};

/**
 * Low-level component to handle effects of mounting the overlay. Do not use on its own.
 */
function ModalOverlayInner({ isOpen, onClose = noop, ...props }: ModalProps) {
  const mouseDownRef = React.useRef<EventTarget | null>(null);
  const overlayRef = React.useRef<HTMLDivElement | null>(null);

  // When overlay is rendered, imperatively add aria-hidden to modal's sibling elements to prevent
  // screen reader from reading anything under the modal. Clean-up effect will return each sibling's
  // aria-hidden attribute to what it was originally, if it existed, or remove it.
  React.useEffect(() => createAriaHider(overlayRef.current), []);

  // Set up event handlers to close the modal when clicking outside of the ModalContent area or when
  // pressing the Escape button
  const onClick: React.MouseEventHandler = (event) => {
    // Make sure user is clicking the overlay or the default modal close button (i.e. "X" icon)
    if (
      mouseDownRef.current === event.target ||
      (event.target as HTMLElement)?.dataset?.modalCloseButton
    ) {
      event.stopPropagation();
      onClose(event);
    }
  };

  const onMouseDown: React.MouseEventHandler = (event) => {
    mouseDownRef.current = event.target;
  };

  const onKeyDown: React.KeyboardEventHandler = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onClose(event);
    }
  };

  return (
    <StyledOverlay
      {...props}
      ref={overlayRef}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onMouseDown={onMouseDown}
    />
  );
}

const StyledOverlay = styled(FadeIn)(({ theme }) => ({
  background: `${theme.colors.fg}e0`,
  backdropFilter: "blur(1px)",
  bottom: 0,
  display: "grid",
  placeItems: "center",
  left: 0,
  overflow: "hidden",
  position: "fixed",
  right: 0,
  top: 0,
  zIndex: 500, // SiteHeader has a zIndex of 500
}));

/**
 * Low-level overlay component if you need more control than the standard Modal
 *
 * You'll need to use the other low-level ModalContent component as well, for example
 * @example
 * ```
 * <StyledModalOverlay isOpen={isOpen}>
 *   <ModalContent>
 *     <p>some content...</p>
 *   </ModalContent>
 * </StyledModalOverlay>
 * ```
 */
function ModalOverlay({
  initialFocusRef,
  isOpen = true,
  ...props
}: ModalProps) {
  // If we're given a ref for initial focus, do so as soon as Modal shows and FocusLock activates
  const onActivation = React.useCallback(() => {
    if (initialFocusRef?.current) {
      initialFocusRef.current.focus();
    }
  }, [initialFocusRef]);

  return isOpen ? (
    <Portal>
      <FocusLock
        disabled={!isOpen}
        autoFocus
        returnFocus
        onActivation={onActivation}
      >
        <RemoveScroll enabled={isOpen} allowPinchZoom>
          <ModalOverlayInner {...props} />
        </RemoveScroll>
      </FocusLock>
    </Portal>
  ) : null;
}

/**
 * Given an open modal overlay element, hide its siblings from screen readers
 */
const createAriaHider = (overlayEl: HTMLElement | null) => {
  if (!overlayEl) return noop;

  // Store any modified element and its original value in an array of tuples for cleanup function
  const modified: [HTMLElement, string | null][] = [];
  // Find the overlay's top-most ancester (i.e. overlay -> scroll-lock -> focus-lock -> *portal*)
  const modalEl = overlayEl.parentNode?.parentNode?.parentNode;

  Array.prototype.forEach.call(
    overlayEl.ownerDocument.querySelectorAll("body > *"),
    (el) => {
      // Skip the modal's element
      if (el === modalEl) return;

      // Get attribute value and skip if element is already hidden
      const val = el.getAttribute("aria-hidden");
      const isHidden = val !== null && val !== "false";
      if (isHidden) return;

      // Add aria-hidden to element and store it and its original value in array
      el.setAttribute("aria-hidden", "true");
      modified.push([el, val]);
    }
  );

  // To cleanup, iterate over any modified elements and undo the aria-hidden modification. If there
  // was no attribute value before, remove it. Otherwise, reset it to its original value.
  return () => {
    modified.forEach(([el, val]) => {
      if (val === null) {
        el.removeAttribute("aria-hidden");
      } else {
        el.setAttribute("aria-hidden", val);
      }
    });
  };
};

type ModalContentProps = React.ComponentProps<typeof StyledContent> &
  Pick<ModalProps, "showCloseButton">;

/**
 * Low-level content component if you need more control than the standard Modal
 *
 * You'll need to nest within the other low-level ModalOverlay component, for example:
 * @example
 * ```
 * <ModalOverlay isOpen={isOpen}>
 *   <StyledModalContent>
 *     <p>some content...</p>
 *   </StyledModalContent>
 * </ModalOverlay>
 * ```
 */
function ModalContent({
  children,
  showCloseButton = true,
  ...props
}: ModalContentProps) {
  // Capture any events bubbling up from within ModalContent. If any event handlers have handled it
  // already and called `event.preventDefault()`, then do not stop propogation and let it be handled
  // by the ModalOverlay which will close the modal.
  const onClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (!event.defaultPrevented) {
      event.stopPropagation();
    }
  };

  // Create a handler for close button in top-right corner that bypasses the above capture and will
  // run the onClose event handler given to ModalOverlay
  const handleClose: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
  };

  return (
    <StyledContent
      aria-modal="true"
      role="dialog"
      tabIndex={-1}
      {...props}
      onClick={onClick}
    >
      {showCloseButton && (
        <CloseButton
          onClick={handleClose}
          variant="blank"
          data-modal-close-button
        >
          <IconClose title="Close" />
        </CloseButton>
      )}
      <ChildrenWrapper showCloseButton={showCloseButton}>
        {children}
      </ChildrenWrapper>
    </StyledContent>
  );
}

const StyledContent = styled.div(({ theme }) => ({
  background: theme.colors.bg,
  height: "100vh",
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  [theme.breakpoints.desktop]: {
    borderRadius: theme.borderRadius.sm,
    boxShadow: theme.boxShadow.dark,
    position: "relative",
    height: "max-content",
    maxHeight: 1000,
    width: "90%",
    maxWidth: 1100,
    margin: "0 auto",
  },
}));

const ChildrenWrapper = styled.div<{ showCloseButton: boolean }>(
  ({ showCloseButton, theme }) => ({
    overflowY: "scroll",
    overflowX: "hidden",
    paddingTop: showCloseButton ? "3.2rem" : theme.spacing.gutter,
    paddingRight: theme.spacing.gutter,
    paddingBottom: `calc(${theme.spacing.gutter} + env(safe-area-inset-bottom))`,
    paddingLeft: theme.spacing.gutter,
  })
);

const CloseButton = styled(Button)(({ theme }) => ({
  backgroundColor: `${theme.colors.bg}80`,
  borderRadius: theme.borderRadius.round,
  color: theme.colors.fg,
  borderBottom: 0,
  width: 34,
  height: 34,
  boxShadow: theme.boxShadow.light,
  position: "absolute",
  top: "1rem",
  right: "1rem",
  zIndex: 2,

  "&:hover,&:focus-within": {
    backgroundColor: `${theme.colors.bg}a0`,
    borderBottom: 0,
    boxShadow: theme.boxShadow.dark,
    color: theme.colors.fg,
  },
}));

/**
 * High-level modal component that renders its content over a standard overlay and prevents
 * accessibility to anything below it, trapping focus and locking scrolling elements below,
 * appending the content as the last child of the document body to avoid z-index issues.
 */
function Modal({
  initialFocusRef,
  isOpen,
  onClose = noop,
  ...props
}: ModalProps) {
  return (
    <ModalOverlay
      initialFocusRef={initialFocusRef}
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalContent {...props} />
    </ModalOverlay>
  );
}

const useModalState = (initialValue = false) => {
  const [isOpen, toggle, setIsOpen] = useToggle(initialValue);

  const open = React.useCallback(() => setIsOpen(true), [setIsOpen]);
  const close = React.useCallback(() => setIsOpen(false), [setIsOpen]);

  const value = React.useMemo(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle]
  );

  return value;
};

// Package low-level components and hooks within the Modal for easier imports and usage
Modal.Content = ModalContent;
Modal.Overlay = ModalOverlay;
Modal.useModalState = useModalState;

export { Modal };
